import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef
} from "react";
import { COLUMNS } from "./columns";
import { IoInformationCircle } from "react-icons/io5";
import { Tooltip } from "react-tooltip";
import {
  showToast,
  formatDateWithDayjs,
  formatCurrencyWithSign,
  downloadFileFromResponse,
  formatCurrencyAmount,
  formatCurrencyWithLocale,
  isOver90Days,
  hasAnyDueDateTooSoon,
  hasAnyOver90Days,
  showAlert,
  isFutureDate
} from "../../../../utils/utils";
import { CashbookListModal } from "../../../modals/CashbookListModal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  useTable,
  useRowSelect,
  useFilters,
  usePagination,
  useExpanded
} from "react-table";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaSortAmountDown,
  FaSort,
  FaSortAmountUpAlt
} from "react-icons/fa";
import FundInvTable from "./fundinvtable/FundInvTable";
import InvPromoCode from "../../../promocode/InvPromoCode";
import ManualUploadInv from "../../../modals/ManualUploadInv";
import CurrentOutStanding from "../../../modals/CurrentOutStanding";
import RequestCreditLimitModal from "../../../modals/RequestCreditLimitModal";
import SyncInvoiceModal from "../../../modals/SyncInvoiceModal";
import useAccountingSoftwareApi from "../../../../hooks/useAccountingSoftwareApi";
import ManualUploadInvInfo from "../../../modals/ManualUploadInvInfo";
import useMixpanelEventAction from "../../../../config/mixpanel/useMixpanelEventAction";
import useGA4EventAction from "../../../../config/GoogleAnalytics/useGA4EventAction";
import FundActionButtons from "./FundActionButtons";
import XeroInvoiceSync from "./XeroInvoiceSync";
import QuoteFooter from "./QuoteFooter";
import CheckboxSelect from "../CheckboxSelect";
import AcceptQuoteInvoiceModal from "../../../modals/AcceptQuoteInvoiceModal";
import PropTypes from "prop-types";
import {
  fundedInvoicesData,
  responseStatus,
  commonErrMsg,
  noDataPresent,
  SearchText,
  clickHere,
  email,
  auPhNumber,
  nzPhNumber,
  promoComponent,
  checklist,
  acceptQuoteModalData,
  alertActions
} from "../../../../locales/en";
import {
  ASC,
  COMPLETED,
  CURRENCY_FORMAT,
  DD_MMM_YYYY,
  DESC,
  DUEDATE,
  INVOICEDATE,
  MAILTO_INFO_EMAIL,
  MIN_INVOICE_THRESHOLD,
  PAGE_INDEX,
  PAGE_SIZE,
  PHONE_NO_AU,
  PHONE_NO_NZ,
  SUBMISSION_DAYS_LENGTH,
  TOTALAMOUNT,
  TRUE
} from "../../../../utils/constants/appConstants";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../../../../redux/userManagementSlice";
import "./fundtable.scss";
import { fetchXeroSyncInvoiceData } from "../../../../redux/xerowritebackSlice";
import privateAxios from "../../../../utils/axiosPrivate";
import {
  getGenerateQuotePDFUrl,
  getSubmittedInvoicesURL
} from "../../../../utils/constants/apiEndPoints";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import {
  DEFAULT_FUND_SORT,
  useFundInvoices
} from "../../../../hooks/invoiceConnect/useFundInvoices";
import { useColumnSort } from "../../../../hooks/useColumnSort";
import { useInvoiceSync } from "../../../../hooks/invoiceConnect/useInvoiceSync";
import { useCreditLimit } from "../../../../hooks/invoiceConnect/useCreditLimit";
import { getFundButtons } from "../../../../utils/constants/fundButtonRules";
import {
  ACTIVE_ONBOARDING_ROUTE,
  COMPLETE_ACCOUNT_SETUP
} from "../../../../route/paths";
import { isOnboardingUserStatus } from "../../../../utils/onboardingVisibility";
import PublicPaginationController from "../../../common/PublicPaginationController";
import PpRequiredModal from "../../../modals/PpRequiredModal";

const renderExpandedColumnValue = (column, row) => {
  const value = row.original[column.accessor];

  if (column.accessor === "contactName") {
    return value || "";
  }

  if (column.accessor === DUEDATE) {
    const dueDateValue = row.original?.repaymentDate || value;
    return dueDateValue ? formatDateWithDayjs(dueDateValue, DD_MMM_YYYY) : "";
  }

  if (column.accessor === INVOICEDATE) {
    return value ? formatDateWithDayjs(value, DD_MMM_YYYY) : "";
  }

  if (column.accessor === TOTALAMOUNT) {
    if (value !== undefined && value !== null && value !== "") {
      return (
        <>
          {CURRENCY_FORMAT}
          {formatCurrencyWithLocale(value)}
        </>
      );
    }
    return (
      <>
        {CURRENCY_FORMAT}
        {formatCurrencyWithLocale(0)}
      </>
    );
  }

  return value;
};

const getSortIcon = (order) => {
  if (order === ASC) return <FaSortAmountUpAlt />;
  if (order === DESC) return <FaSortAmountDown />;
  return <FaSort />;
};

const getDisabledRowTooltip = ({ isFuture, isOver90, isUnderThreshold }) => {
  if (isFuture) return acceptQuoteModalData?.futureDays;
  if (isOver90) return acceptQuoteModalData?.before90Days;
  if (isUnderThreshold) return acceptQuoteModalData?.minInvoiceThreshold;
  return "";
};

const SORTABLE_COLUMN_STATE = {
  invoiceNumber: null,
  debatorName: null,
  totalAmount: null,
  [DUEDATE]: ASC,
  comment: null
};

const FundManualTable = ({
  setActiveTab,
  profileUserData,
  onboardingStatus
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS, []);
  const location = useLocation();
  const BookIdDetails = location.state?.BookIdDetails;
  const isCompanyFiles = location.state?.companyFiles;

  const [isChecked, setIsChecked] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [persistentSelectedRows, setPersistentSelectedRows] = useState([]);
  const { GetConnectedSoftware } = useAccountingSoftwareApi();
  const [isConnectedSoftware, setIsConnectedSoftware] = useState(false);

  const [disconnectedAt, setDisconnectedAt] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const dispatch = useDispatch();

  const [selectedInvData, setSelectedInvData] = useState([]);
  const [quotedData, setQuotedData] = useState([]);
  const [promoCodeSubmit, setPromoCodeSubmit] = useState(false);
  const [connectedSoftName, setConnectedSoftName] = useState("");

  const checkSoftwareConnectedCalled = useRef(false);
  const { submitInvoiceMixpanelEvent } = useMixpanelEventAction();
  const { submitInvoiceGA4Event } = useGA4EventAction();
  const currentState = useRef(true);
  const [xeroInvoiceJobId, setXeroInvoiceJobId] = useState(
    localStorage.getItem("xeroInvoiceSyncJobId")
  );

  const [promoCode, setPromoCode] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();

  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [cashBookId, setCashBookId] = useState(false);
  const [addManualModalInfo, setAddManualModalInfo] = useState(false);
  const [isManuallyClosed, setIsManuallyClosed] = useState(false);
  const [addManualMoadal, setAddManualMoadal] = useState(false);
  const [addOutStanding, setAddOutStanding] = useState(false);
  const [addCreditModal, setAddCreditModal] = useState(false);
  const [addSyncInvoice, setAddSyncInvoice] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);
  const [acceptQuoteModal, setAcceptQuoteModal] = useState(false);
  const [loadingAcceptQuote, setLoadingAcceptQuote] = useState({
    loginBtn: false
  });
  const [quoteDownloadLoading, setQuoteDownloadLoading] = useState(false);

  const [ppRequiredModal, setPpRequiredModal] = useState(false);
  const [popRequiredQuoteIds, setPopRequiredQuoteIds] = useState([]);
  const [creditModalFromPop, setCreditModalFromPop] = useState(false);
  const handleOpenRequiredModal = () => {
    setPpRequiredModal((prev) => {
      if (prev) {
        setAcceptQuoteModal(true);
        if (showOnboardingPrompt) {
          navigate(ACTIVE_ONBOARDING_ROUTE);
        }
      }
      return !prev;
    });
  };

  // Custom Hooks
  const {
    data,
    loading,
    totalPages,
    searchQuery,
    setSearchQuery,
    loadInvoices
  } = useFundInvoices();
  const { creditData, loadCredit } = useCreditLimit();

  const { columnOrders, handleSort } = useColumnSort(SORTABLE_COLUMN_STATE);
  const sortableColumns = Object.keys(SORTABLE_COLUMN_STATE);

  const sortParams = useMemo(() => {
    const activeSort = Object.entries(columnOrders).find(([, order]) => order);
    return activeSort
      ? { sortBy: activeSort[0], order: activeSort[1] }
      : DEFAULT_FUND_SORT;
  }, [columnOrders]);

  const reloadInvoices = useCallback(
    (page, size, query = searchQuery) =>
      loadInvoices(page, size, query, sortParams),
    [loadInvoices, searchQuery, sortParams]
  );

  const handleXeroSyncInvoice = useCallback(async () => {
    try {
      const response = await dispatch(fetchXeroSyncInvoiceData()).unwrap();
      const result = response?.data?.data;
      setXeroInvoiceJobId(result?.jobId);
      showToast(response?.data.message, responseStatus?.success);
    } catch (error) {
      showToast(
        error?.response?.data.message || responseStatus?.errorText,
        responseStatus?.error
      );
    }
  }, [dispatch]);

  const { syncInvoices: handleSyncInvoices, loading: syncLoading } =
    useInvoiceSync({
      connectedSoftName,
      handleXeroSyncInvoice,
      onSuccess: (res) => {
        reloadInvoices(0, pageSize);
        showToast(res.data.message, responseStatus?.success);
      }
    });

  const userStatusId = profileUserData?.userStatusId;
  const primaryUserId = profileUserData?.primaryUserId;
  const refferalParrtner = profileUserData?.userAccountMeta?.referralCode;
  const isUploadTrue = profileUserData?.systemSettings?.invoiceUpload;

  const liveProfile = useSelector((state) => state.userManagement.profile);
  const forceApplicationProcess =
    liveProfile?.systemSettings?.forceApplicationProcess;
  // Fund starts onboardingStatus empty and a failed read leaves it unset, so an
  // empty value means unknown rather than unfinished. Acting on unknown sends a
  // customer to a page that may hold nothing for them, so each onboarding
  // prompt below waits for a real status. See docs/decisions/0059.
  const isForcedReapplication =
    forceApplicationProcess === true || forceApplicationProcess === TRUE;
  const isOnboardingUnfinished =
    Boolean(onboardingStatus) && onboardingStatus !== COMPLETED;
  const isForcedAfterCompletion =
    isForcedReapplication && onboardingStatus === COMPLETED;
  // Account status decides whether this customer sees onboarding at all. Only
  // Pending, Archived and Customer DD do, which is the same rule the tasks
  // banner and the post-login redirect apply, so a decided account gets no
  // prompt here and is not carried to a page it can no longer reach. Forced
  // re-application is a separate flow on a completed account and keeps its own
  // destination. See docs/decisions/0060.
  const showOnboardingPrompt =
    isOnboardingUnfinished && isOnboardingUserStatus(userStatusId);

  const createToggle = (setState) =>
    useCallback(() => setState((prev) => !prev), [setState]);

  // Toggle functions for modals
  const toggleManualModal = createToggle(setAddManualMoadal);
  const toggleOutStanding = createToggle(setAddOutStanding);
  const toggleCreditModal = createToggle(setAddCreditModal);
  const toggleSyncInvoice = createToggle(setAddSyncInvoice);
  const toggleAcceptQuoteModal = createToggle(setAcceptQuoteModal);
  const toggleCaskbookList = createToggle(setCashBookId);

  const handleRequestCreditLimitFromPop = () => {
    setPpRequiredModal(false);
    setCreditModalFromPop(true);
    setAddCreditModal(true);
  };

  const openCreditModal = useCallback(() => {
    setCreditModalFromPop(false);
    setAddCreditModal(true);
  }, []);

  const handleCreditModalCancel = useCallback(() => {
    if (creditModalFromPop) {
      setPpRequiredModal(true);
    }
    setCreditModalFromPop(false);
  }, [creditModalFromPop]);

  // Get button visibility configuration
  const buttonsToShow = useMemo(
    () =>
      getFundButtons({
        isConnected: isConnectedSoftware,
        isUploadEnabled: isUploadTrue,
        disconnectedAt,
        isPrimaryUser: primaryUserId !== null && primaryUserId !== undefined
      }),
    [isConnectedSoftware, isUploadTrue, disconnectedAt, primaryUserId]
  );

  const handleManualClose = useCallback(() => {
    setAddManualModalInfo(false);
    setIsManuallyClosed(true);
  }, []);

  const isMultipleBookId = useCallback(() => {
    if (BookIdDetails !== null && BookIdDetails !== undefined) {
      toggleCaskbookList();
    }
  }, [BookIdDetails, toggleCaskbookList]);

  const selectedTotalInvoiceAmount = useCallback((persistentSelectedRows) => {
    let total = 0;
    persistentSelectedRows?.forEach((row) => {
      total += parseFloat(row.outstandingAmount) || row.totalAmount;
    });
    return (total * 0.9).toFixed(2);
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,

      getRowId: (invoice, index) => String(invoice?.id ?? index),
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },
    useFilters,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  // A new sort reorders the whole result set, so the page the customer was on no
  // longer points at the same invoices — send them back to the first page.
  const handleColumnSort = useCallback(
    (columnId) => {
      // Clicking a column that is already descending clears it, which drops the
      // whole table back to the due-date fallback. Land due date on ascending
      // rather than on "no sort", so the state matches what is displayed and
      // requested — otherwise the next due-date click would re-ask for the order
      // already on screen and the header would look unresponsive.
      const clearsToFallback = columnOrders[columnId] === DESC;

      handleSort(columnId);
      if (clearsToFallback) {
        handleSort(DUEDATE);
      }
      gotoPage(PAGE_INDEX);
    },
    [handleSort, gotoPage, columnOrders]
  );

  const checkSoftwareConnected = useCallback(async () => {
    if (checkSoftwareConnectedCalled.current) return;
    checkSoftwareConnectedCalled.current = true;
    try {
      const response = await GetConnectedSoftware();
      const isConnected = response?.data?.data?.isConnected ?? false;
      const disconnectedAt = response?.data?.data?.disconnectedAt;
      setConnectedSoftName(response?.data?.data?.accountingSoftwareType);
      setIsConnectedSoftware(isConnected);
      setDisconnectedAt(disconnectedAt);
    } catch {
      setIsConnectedSoftware(false);
    }
  }, [GetConnectedSoftware]);

  useEffect(() => {
    const selectedCount = persistentSelectedRows.length;

    setShowButtons(selectedCount > 0);

    if (selectedCount > 0 && !addManualModalInfo && !isManuallyClosed) {
      setAddManualModalInfo(true);
    }

    if (selectedCount === 0 && addManualModalInfo) {
      setAddManualModalInfo(false);
    }
  }, [addManualModalInfo, isManuallyClosed, persistentSelectedRows.length]);

  const handleCheckboxChange = () => {
    setIsChecked((prev) => !prev);
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  };

  // Handle changes in the search input
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setTempSearchQuery(value);
      if (value.trim() === "") {
        setSearchQuery("");
      }
    },
    [setSearchQuery]
  );

  // Handle key press events in the search input
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        const trimmedQuery = tempSearchQuery.trim();
        setHasSearched(true);
        setSearchQuery(trimmedQuery);
        gotoPage(0);
      }
    },
    [tempSearchQuery, setSearchQuery, gotoPage]
  );

  // Initialize software connection and load data
  useEffect(() => {
    if (currentState.current) {
      isMultipleBookId();
      currentState.current = false;
    }
    checkSoftwareConnected();
  }, [checkSoftwareConnected, isMultipleBookId]);

  useEffect(() => {
    dispatch(getProfile());
  }, []);

  useEffect(() => {
    reloadInvoices(pageIndex, pageSize, searchQuery);
    loadCredit();
  }, [
    pageIndex,
    pageSize,
    searchQuery,
    xeroInvoiceJobId,
    reloadInvoices,
    loadCredit
  ]);

  // Auto sync for Xero if needed
  useEffect(() => {
    if (location.state?.autoSyncInvoice) {
      handleXeroSyncInvoice();
    }
  }, [location.state?.autoSyncInvoice, handleXeroSyncInvoice]);

  const handleAcceptQuote = async () => {
    if (!isChecked) {
      setCheckboxError(true);
      return;
    }
    setCheckboxError(false);

    const tooSoon = hasAnyDueDateTooSoon(
      persistentSelectedRows,
      SUBMISSION_DAYS_LENGTH
    );
    const over90Days = hasAnyOver90Days(persistentSelectedRows);

    if (over90Days) {
      showAlert(
        "",
        acceptQuoteModalData?.before90Days,
        alertActions?.warning,
        alertActions?.ok
      );
      return;
    }

    if (tooSoon) {
      showAlert(
        "",
        acceptQuoteModalData?.content,
        alertActions?.warning,
        alertActions?.ok
      );
      return;
    }
    try {
      setLoadingAcceptQuote((prev) => ({ ...prev, loginBtn: true }));
      const selectedInvoicesRequestBody = [];
      persistentSelectedRows.forEach((invoice, index) => {
        const feeSaved = quotedData?.[index]?.feeSaved || 0.0;
        selectedInvoicesRequestBody.push({
          invoiceId: invoice.id,
          invoiceRepaymentDate: invoice.dueDate,
          promoCodeDiscountAmount: feeSaved,
          otherDiscountAmount: 0.0
        });
      });

      const response = await privateAxios.post(getSubmittedInvoicesURL, {
        invoiceDetails: selectedInvoicesRequestBody,
        promoCode: promoCode
      });
      if (response.status === 200) {
        const submitResponse = response?.data?.data || {};
        submitInvoiceMixpanelEvent(submitResponse);
        submitInvoiceGA4Event(response?.data?.data);
        gotoPage(0);
        reloadInvoices(0, pageSize);
        setPersistentSelectedRows([]);
        setPromoCode("");
        if (submitResponse?.popRequired === true) {
          const quoteIds = submitResponse?.quotes?.map((quote) => quote.id);
          setPopRequiredQuoteIds(quoteIds);
          setPpRequiredModal(true);
        } else {
          setAcceptQuoteModal(true);
          if (showOnboardingPrompt) {
            navigate(ACTIVE_ONBOARDING_ROUTE);
          }
        }
      }

      showToast(response.data.message, responseStatus?.success);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        commonErrMsg?.item4;
      showToast(errorMessage, responseStatus?.error);
    } finally {
      setLoadingAcceptQuote((prev) => ({ ...prev, loginBtn: false }));
    }
  };

  const handleQuoteDownload = async () => {
    try {
      setQuoteDownloadLoading(true);
      const selectedInvoicesRequestBody = selectedInvData;

      const response = await privateAxios.post(
        getGenerateQuotePDFUrl,
        selectedInvoicesRequestBody,
        {
          responseType: "blob"
        }
      );
      downloadFileFromResponse(response, "quote.pdf");
      showToast(checklist?.errors.fileDownload, responseStatus?.success);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        commonErrMsg?.item4;
      showToast(errorMessage, responseStatus?.error);
    } finally {
      setQuoteDownloadLoading(false);
    }
  };

  const paginationParams = {
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageIndex,
    pageCount,
    pageOptions,
    setPageSize,
    pageSize,
    dataLength: data.length
  };

  return (
    <>
      {xeroInvoiceJobId !== null && (
        <XeroInvoiceSync
          jobId={xeroInvoiceJobId}
          setXeroInvoiceJobId={setXeroInvoiceJobId}
        />
      )}
      {showButtons && (
        <div className="upper-quote-wrap mb-5">
          <div className="quote-heading">
            <h4>{fundedInvoicesData?.toFund?.title}</h4>
          </div>
          <div className="quote-body">
            <div className="quote-summary-container">
              <p>
                {`${fundedInvoicesData?.toFund?.content1} `}
                <span className="amount">
                  {formatCurrencyAmount(
                    selectedTotalInvoiceAmount(persistentSelectedRows),
                    CURRENCY_FORMAT
                  )}
                </span>
                {` ${fundedInvoicesData?.toFund?.content2}`}
              </p>
              <p>
                <small>{fundedInvoicesData?.toFund?.content3}</small>
              </p>
            </div>
          </div>
          <div className="quote-footer text-center">
            <QuoteFooter
              checkboxId="checkbox1"
              isChecked={isChecked}
              onCheckboxChange={handleCheckboxChange}
              checkboxError={checkboxError}
              onDownload={handleQuoteDownload}
              onAccept={handleAcceptQuote}
              quoteLoading={quoteDownloadLoading}
              isLoading={loadingAcceptQuote.loginBtn}
              contentKey="content4"
            />
          </div>
        </div>
      )}

      <div className={showButtons ? "hidden" : "visible"}>
        <div className="instraction-text">
          <span>
            <strong>{fundedInvoicesData?.toFund?.content6}</strong>
            <span className="ms-2">{fundedInvoicesData?.toFund?.content7}</span>
          </span>
        </div>
      </div>
      <table
        className="mt-2 mb-3 fundtable fundtable-invoice no-table-header-bg"
        {...getTableProps()}
      >
        <thead ref={headerRef}>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                <th />
                <th />
                {headerGroup.headers.map((column) => {
                  const isSortable = sortableColumns.includes(column.id);
                  return (
                    <th key={column.id} {...column.getHeaderProps()}>
                      <span
                        onClick={
                          isSortable
                            ? () => handleColumnSort(column.id)
                            : undefined
                        }
                        className={`header-sort-wrap${isSortable ? " cursor-pointer" : ""}`}
                      >
                        {isSortable && (
                          <span className="short-icon">
                            {/* Read the arrow off the sort actually in force, so
                                clearing a column can't leave the fallback order
                                applied under a neutral arrow. */}
                            {getSortIcon(
                              sortParams.sortBy === column.id
                                ? sortParams.order
                                : null
                            )}
                          </span>
                        )}
                        <span>{column.render("Header")}</span>
                      </span>
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading && <TableSkeleton rows={6} columns={visibleColumnCount} />}
          {!loading && page.length === 0 && (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {noDataPresent}
              </td>
            </tr>
          )}
          {!loading &&
            page.length > 0 &&
            page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              const isOver90 = isOver90Days({
                dueDate: row.original?.dueDate,
                invoiceDate: row.original?.invoiceDate
              });

              const isUnderThreshold =
                parseFloat(row.original.totalAmount) <= MIN_INVOICE_THRESHOLD;
              const isDisabledRow =
                isFutureDate(row.original?.invoiceDate) ||
                isOver90 ||
                isUnderThreshold;

              const tooltipMsg = getDisabledRowTooltip({
                isFuture: isFutureDate(row.original?.invoiceDate),
                isOver90,
                isUnderThreshold
              });
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={`parentrow ${isDisabledRow ? "row-disabled" : ""}`}
                    {...row.getRowProps()}
                  >
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={() => toggleRow(row.id)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>
                    <td
                      data-tooltip-id={
                        isDisabledRow ? `invoice-tooltip` : undefined
                      }
                      data-tooltip-content={tooltipMsg}
                      data-tooltip-place="top"
                    >
                      <CheckboxSelect
                        invoiceData={row.original}
                        selectedRows={persistentSelectedRows}
                        setSelectedRows={setPersistentSelectedRows}
                        isUnderThreshold={isUnderThreshold}
                      />
                    </td>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.getCellProps().key}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => (
                            <span
                              key={column.accessor}
                              data-label={column.Header}
                            >
                              {renderExpandedColumnValue(column, row)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
      <div className="d-flex flex-wrap mt-5 mb-4 justify-content-sm-between search">
        <div className="d-flex flex-wrap search mb-1">
          {data.length > 0 && (
            <PublicPaginationController
              {...paginationParams}
              showButtons={false}
            />
          )}
        </div>
        <div className="d-flex flex-wrap search mb-1">
          {data.length > 0 && (
            <PublicPaginationController {...paginationParams} showButtons />
          )}
        </div>
        {(hasSearched || data.length > 0) && (
          <div className="search-box mb-1">
            <input
              type="search"
              value={tempSearchQuery}
              onChange={handleSearchChange}
              placeholder={SearchText}
              onKeyDown={handleKeyDown}
              className="rounded-0"
            />
          </div>
        )}
      </div>

      {!showButtons && (
        <FundActionButtons
          buttons={buttonsToShow}
          onManual={toggleManualModal}
          onSync={handleSyncInvoices}
          syncLoading={syncLoading}
          xeroInvoiceJobId={xeroInvoiceJobId}
        />
      )}

      <div
        className={`fund-bottom-group ${showButtons ? "hidden" : "visible"}`}
      >
        {userStatusId === 2 && (
          <div className="credit_limit">
            <p>
              {` ${fundedInvoicesData?.toFund?.content8} `}
              <strong>
                {formatCurrencyAmount(creditData?.creditLimit, CURRENCY_FORMAT)}
              </strong>
            </p>
            <p>
              {` ${fundedInvoicesData?.toFund?.content9} `}
              <span onClick={toggleOutStanding}>
                <IoInformationCircle />
              </span>
              :
              <strong>
                {formatCurrencyAmount(
                  creditData?.totalFundedInvoices,
                  CURRENCY_FORMAT
                )}
              </strong>
            </p>

            <p>
              {` ${fundedInvoicesData?.toFund?.content10} `}
              {creditData?.totalFundedInvoices > creditData?.availableCredit &&
              Number(creditData?.availableCredit) < 0 ? (
                <strong className="text-danger">
                  {CURRENCY_FORMAT}
                  {formatCurrencyWithSign(creditData?.availableCredit)}
                </strong>
              ) : (
                <strong>
                  {formatCurrencyAmount(
                    creditData?.availableCredit,
                    CURRENCY_FORMAT
                  )}
                </strong>
              )}
            </p>
            {creditData?.hasCompletedFundingBefore && (
              <Link
                to=""
                onClick={(e) => {
                  e.preventDefault();
                  openCreditModal();
                }}
                className="border-btn-primary"
              >
                {fundedInvoicesData?.toFund?.content11}
              </Link>
            )}
          </div>
        )}

        <div className="invoice-info-msg col-md-8">
          <p>
            {` ${fundedInvoicesData?.toFund?.content12} `}
            <Link
              to=""
              onClick={(e) => {
                e.preventDefault();
                toggleSyncInvoice();
              }}
            >
              {clickHere}
            </Link>
          </p>
          {showOnboardingPrompt || isForcedAfterCompletion ? (
            <p>
              {`${fundedInvoicesData?.toFund?.content13} `}
              <Link
                to={
                  showOnboardingPrompt
                    ? ACTIVE_ONBOARDING_ROUTE
                    : COMPLETE_ACCOUNT_SETUP
                }
              >
                {clickHere}
              </Link>
            </p>
          ) : null}
          <p>
            {`${fundedInvoicesData?.toFund?.content14} `}
            <Link to={MAILTO_INFO_EMAIL}>{email}</Link>
            {` ${fundedInvoicesData?.toFund?.content15} `}
            <Link to={PHONE_NO_AU}>{auPhNumber}</Link>
            {` ${fundedInvoicesData?.toFund?.content16} `}
            <Link to={PHONE_NO_NZ}>{nzPhNumber}</Link>
          </p>
        </div>
      </div>
      {showButtons && persistentSelectedRows.length > 0 && (
        <div className="upper-quote-wrap mb-5">
          <div className="quote-table-wrap">
            <FundInvTable
              setSelectedInvData={setSelectedInvData}
              selectInvoices={persistentSelectedRows}
              setPersistentSelectedRows={setPersistentSelectedRows}
              loadInvoices={() =>
                reloadInvoices(pageIndex, pageSize, searchQuery)
              }
              quotedData={quotedData}
              setQuotedData={setQuotedData}
              refferalParrtner={refferalParrtner}
              promoCodeSubmit={promoCodeSubmit}
            />
          </div>

          <div className="promocode-wrap">
            <InvPromoCode
              selectInvoices={persistentSelectedRows}
              setSelectedInvData={setSelectedInvData}
              setQuotedData={setQuotedData}
              promoCodeSubmit={promoCodeSubmit}
              setPromoCodeSubmit={setPromoCodeSubmit}
              promotitle={promoComponent?.title}
              promoplaceholder={promoComponent?.promoCodePlaceholder}
              setPromoCode={setPromoCode}
              promoCode={promoCode}
            />
          </div>

          <QuoteFooter
            checkboxId="checkbox2"
            isChecked={isChecked}
            onCheckboxChange={handleCheckboxChange}
            checkboxError={checkboxError}
            onDownload={handleQuoteDownload}
            onAccept={handleAcceptQuote}
            quoteLoading={quoteDownloadLoading}
            isLoading={loadingAcceptQuote.loginBtn}
            contentKey="content17"
          />
        </div>
      )}

      <ManualUploadInv
        isOpen={addManualMoadal}
        toggle={toggleManualModal}
        loadInvoceData={() => reloadInvoices(0, pageSize)}
        gotoPage={gotoPage}
        setPageSize={setPageSize}
      />

      <ManualUploadInvInfo
        isOpen={addManualModalInfo}
        toggle={handleManualClose}
      />

      <CurrentOutStanding
        isOpen={addOutStanding}
        toggle={toggleOutStanding}
        setActiveTab={setActiveTab}
        toggleOutStanding={toggleOutStanding}
      />

      <RequestCreditLimitModal
        isOpen={addCreditModal}
        toggle={toggleCreditModal}
        onCancel={handleCreditModalCancel}
      />

      <SyncInvoiceModal isOpen={addSyncInvoice} toggle={toggleSyncInvoice} />

      <CashbookListModal
        isOpen={cashBookId}
        toggle={toggleCaskbookList}
        CashbookIdDetails={BookIdDetails}
        isCompanyFiles={isCompanyFiles}
        handleSyncInvoices={handleSyncInvoices}
      />

      <AcceptQuoteInvoiceModal
        isOpen={acceptQuoteModal}
        toggle={toggleAcceptQuoteModal}
        user={profileUserData}
      />

      <PpRequiredModal
        isOpen={ppRequiredModal}
        toggle={handleOpenRequiredModal}
        quoteIds={popRequiredQuoteIds}
        onRequestCreditLimit={handleRequestCreditLimitFromPop}
        canRequestCreditLimit={Boolean(creditData?.hasCompletedFundingBefore)}
      />

      <Tooltip id="invoice-tooltip" />
    </>
  );
};

FundManualTable.propTypes = {
  setActiveTab: PropTypes.func.isRequired,
  profileUserData: PropTypes.shape({
    userStatusId: PropTypes.number,
    primaryUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    systemSettings: PropTypes.shape({
      invoiceUpload: PropTypes.bool
    }),
    userAccountMeta: PropTypes.shape({
      referralCode: PropTypes.string
    })
  }).isRequired,
  onboardingStatus: PropTypes.string.isRequired
};

export default FundManualTable;
